import lenis from "./lenis.js";

/** Matches `_agenda-tabs.scss`: desktop sticky on `--inner`, mobile sticky on `.agenda__nav`. */
const AGENDA_MOBILE_MQ = "(max-width: 768px)";

function getFixedSiteHeaderOffsetPx() {
  const header = document.querySelector(".site-header");
  if (!(header instanceof HTMLElement)) {
    return 0;
  }
  const pos = getComputedStyle(header).position;
  if (pos !== "fixed" && pos !== "sticky") {
    return 0;
  }
  return Math.round(header.getBoundingClientRect().height);
}

/**
 * Scrolls so the top of the agenda section sits just below the fixed header (if any).
 */
function scrollAgendaSectionIntoView(layout) {
  if (!(layout instanceof HTMLElement)) {
    return;
  }
  const target =
    layout.querySelector(".section-intro__title-wrap") ||
    layout.querySelector(".section-intro") ||
    layout;

  const lenisInstance = lenis();
  if (lenisInstance) {
    lenisInstance.scrollTo(target, {
      offset: -getFixedSiteHeaderOffsetPx(),
      immediate: true,
    });
  } else {
    const y = target.getBoundingClientRect().top + window.scrollY - getFixedSiteHeaderOffsetPx();
    window.scrollTo({
      top: Math.max(0, y),
      behavior: "instant",
    });
  }
}


function getStickyAgendaNavElement(nav) {
  const inner = nav.querySelector(".agenda__nav--inner");
  const mobile = window.matchMedia(AGENDA_MOBILE_MQ).matches;
  const innerSticky = inner instanceof HTMLElement && getComputedStyle(inner).position === "sticky";
  const navSticky = nav instanceof HTMLElement && getComputedStyle(nav).position === "sticky";

  if (mobile) {
    if (navSticky) return nav;
    if (innerSticky) return inner;
  } else {
    if (innerSticky) return inner;
    if (navSticky) return nav;
  }
  return inner instanceof HTMLElement ? inner : nav;
}

/**
 * Toggles `agenda-tabs-sticky` on `.agenda__nav` if its sticky element is pinned at CSS `top`.
 */


function updateAgendaNavStickyClass(nav) {
  const stickyEl = getStickyAgendaNavElement(nav);
  if (!(stickyEl instanceof HTMLElement)) {
    nav.classList.remove("agenda-tabs-sticky");
    return;
  }

  const style = getComputedStyle(stickyEl);
  const topPx = parseFloat(style.top);
  if (!Number.isFinite(topPx)) {
    nav.classList.remove("agenda-tabs-sticky");
    return;
  }

  const rect = stickyEl.getBoundingClientRect();
  /** Slightly wider tolerance on narrow viewports (zoom, safe-area, browser chrome). */
  const epsilon = window.matchMedia(AGENDA_MOBILE_MQ).matches ? 3 : 2;
  nav.classList.toggle("agenda-tabs-sticky", Math.abs(rect.top - topPx) <= epsilon);
}

/**
 * Syncs `.agenda__nav-item` with `.agenda-item__list-inner[data-day]`, activating matching pairs.
 */
export function initAgenda() {
  document.querySelectorAll(".agenda-layout").forEach((layout) => {
    const nav = layout.querySelector(".agenda__nav");
    const navItems = layout.querySelectorAll(".agenda__nav-item[id]");
    const panels = layout.querySelectorAll(".agenda-item__list-inner[data-day]");

    if (!nav || navItems.length === 0 || panels.length === 0) {
      return;
    }

    const setActiveDay = (dayId) => {
      if (!dayId) return;
      navItems.forEach(item => item.classList.toggle("active", item.id === dayId));
      panels.forEach(panel => {
        panel.classList.toggle("active", panel.getAttribute("data-day") === dayId);
      });
    };

    // Prefer item with initial "active" class, else fallback to first item
    let initialDayId = Array.from(navItems).find(item => item.classList.contains("active") && item.id)?.id || navItems[0].id;
    setActiveDay(initialDayId);

    navItems.forEach((item) => {
      item.addEventListener("click", () => {
        // Scroll while the current panel is still in layout so Lenis can resolve
        // the correct target. Swapping first collapses page height and throws the
        // viewport past this section before scroll runs.
        scrollAgendaSectionIntoView(layout);
        setActiveDay(item.id);
        requestAnimationFrame(() => {
          scrollAgendaSectionIntoView(layout);
        });
      });
    });

    if (nav instanceof HTMLElement) {
      let stickyRaf = 0;
      const scheduleStickyUpdate = () => {
        if (!stickyRaf) {
          stickyRaf = requestAnimationFrame(() => {
            stickyRaf = 0;
            updateAgendaNavStickyClass(nav);
          });
        }
      };

      window.addEventListener("scroll", scheduleStickyUpdate, { passive: true });
      window.addEventListener("resize", scheduleStickyUpdate);

      const mq = window.matchMedia(AGENDA_MOBILE_MQ);
      if (typeof mq.addEventListener === "function") {
        mq.addEventListener("change", scheduleStickyUpdate);
      } else {
        mq.addListener(scheduleStickyUpdate);
      }

      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", scheduleStickyUpdate);
      }

      scheduleStickyUpdate();
    }
  });
}
